'use client';

import { useUser } from '@auth0/nextjs-auth0/client';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import React, { Suspense, useCallback } from 'react';
import useSWRInfinite from 'swr/infinite';

import { FilterButtonSkeleton } from '@/features/filter';
import { PublicationsGrid } from '@/features/list-publication';
import { FilterContextProvider } from '@/entities/filters';
import { InfiniteScroll } from '@/entities/infinity-scroll';
import { GetPublicationsReturnItemType, Publication } from '@/shared/types';
import { Icons } from '@/shared/ui';
import { LoadMoreButton } from '@/shared/ui';

const DynamicFilterComponent = dynamic(
  () => import('@/features/filter').then((mod) => mod.FilterComponent),
  {
    loading: () => <FilterButtonSkeleton />,
    ssr: false
  }
);

const DynamicSearchComponent = dynamic(
  () => import('@/features/search').then((mod) => mod.SearchDropdown),
  {
    loading: () => (
      <div className="h-10 w-full animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700"></div>
    ),
    ssr: false
  }
);

const fetcher = (url: string) =>
  fetch(url)
    .then((res) => {
      return res.json();
    })
    // eslint-disable-next-line no-console
    .catch((error) => console.error(error));

const ClientSidePublications = () => {
  const { user } = useUser();
  const searchParams = useSearchParams();
  const [searchQuery, setSearchQuery] = React.useState('');

  const current = new URLSearchParams(Array.from(searchParams.entries()));
  const isMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : false;

  const selectedBreeds = current.get('breeds') ?? '';
  const animalType = current.get('animal-type') as 'all' | 'dog' | 'cat' | undefined;
  const pubType = current.get('publication-type') as
    | 'all'
    | 'продажа'
    | 'дарение'
    | 'случка'
    | undefined;

  const PER_PAGE = isMobile ? 4 : 6;

  const getKey = (pageIndex: number, previousPageData: { publications: Publication[] } | null) => {
    if (previousPageData && previousPageData.publications.length < PER_PAGE) {
      return null;
    }
    const offset = pageIndex * PER_PAGE;
    return `/api/publication/get-publications-paginated?breeds=${encodeURIComponent(
      selectedBreeds
    )}&animal-type=${animalType ?? 'all'}&publication-type=${pubType ?? 'all'}&limit=${PER_PAGE}&offset=${offset}`;
  };

  const { data, setSize, isLoading, isValidating } = useSWRInfinite<{
    publications: GetPublicationsReturnItemType[];
  }>(getKey, fetcher, {
    revalidateOnReconnect: true,
    revalidateOnFocus: false
  });

  const loadMorePublications = useCallback(() => {
    setSize((prev) => prev + 1);
  }, [setSize]);

  return (
    <>
      <div className="flex justify-between rounded-sm bg-slate-200 dark:bg-slate-800">
        <Suspense fallback={<Icons.InfiniteLoadingDotsIcon />}>
          <FilterContextProvider>
            <DynamicFilterComponent />
          </FilterContextProvider>
          <div className="max-w-md flex-1 px-4 py-2">
            <Suspense
              fallback={
                <div className="h-10 w-full animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700"></div>
              }
            >
              <DynamicSearchComponent
                query={searchQuery}
                onQueryChange={setSearchQuery}
                onClose={() => setSearchQuery('')}
              />
            </Suspense>
          </div>
        </Suspense>
      </div>
      <div className="container m-auto max-w-6xl p-4 text-center">
        {data?.map((pubs, index) => (
          <PublicationsGrid
            key={index}
            publications={pubs.publications}
            currentUserEmail={user?.email}
          />
        ))}

        <div id="load-more" style={{ height: '20px', marginBottom: '20px' }}></div>

        <InfiniteScroll
          loadMore={loadMorePublications}
          isLoading={isLoading}
          elementId="load-more"
        />

        {isValidating && (
          <div className="flex h-[80vh] w-full justify-center">
            <Icons.InfiniteLoadingDotsIcon className="text-blue-main dark:text-gray-300" />
          </div>
        )}

        {!isLoading && <LoadMoreButton disabled={false} loadMore={loadMorePublications} />}
      </div>
    </>
  );
};

export default ClientSidePublications;
